import { notifications } from "@mantine/notifications";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { useFeature } from "flagged";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import DatasourceAccessPendingModal from "src/components/DatasourceAccessPendingModal";
import DatasourceRequestAccessModal from "src/components/DatasourceRequestAccessModal";
import { useDatasources } from "src/hooks/useDatasources";
import { useAvailableAlwaysOnDatasourceIds, useUser, useUserDatasources } from "src/hooks/useUser";
import { DATASOURCE_BROWSER_ROUTES } from "src/services/datasources/datasourceBrowserRoutes";
import { resolveDatasourceTab } from "src/services/datasources/resolveDatasourceTab";
import {
    type DatasourceKind,
    datasourceKindOfTab,
    visibleDatasources,
} from "src/services/datasources/visibleDatasources";
import { DocumentsRoute } from "./DocumentsRoute";

interface DeniedDatasource {
    id: string;
    name: string;
    description: string;
    ingestion_status: string;
    pending: boolean;
}

export const DatasourceBrowser = ({ kind }: { kind: DatasourceKind }) => {
    const route = DATASOURCE_BROWSER_ROUTES[kind];
    const navigate = useNavigate();
    // biome-ignore lint/suspicious/noExplicitAny: route ids are resolved from a lookup table
    const { tab } = useSearch({ from: route.searchFrom as any }) as { tab?: string };

    const assetsViewerEnabled = Boolean(useFeature("ASSETS_VIEWER"));

    const { data: chatVectorIndex = [], isSuccess } = useDatasources();
    const { data: userDatasourceDetails, isSuccess: isUserSuccess } = useUserDatasources();
    const availableAlwaysOnIds = useAvailableAlwaysOnDatasourceIds();
    const { data: user } = useUser();

    // The assets viewer only exists behind the ASSETS_VIEWER feature. With it off there
    // is no separate asset browser, so fold any /assets visit back into the documents viewer.
    const assetsViewerUnavailable = kind === "asset" && !assetsViewerEnabled;
    useEffect(() => {
        if (!assetsViewerUnavailable) return;
        // biome-ignore lint/suspicious/noExplicitAny: generated router types omit search params
        navigate({ to: "/documents" as any, search: (tab ? { tab } : {}) as any, replace: true });
    }, [assetsViewerUnavailable, tab, navigate]);

    // Role-granted datasource ids, sourced live from /v1/user (via useUserDatasources)
    // rather than the one-time-synced "userDatasources" localStorage snapshot, so
    // access changes (role updates, approved access requests, saved preferences)
    // are reflected without requiring a full page reload.
    const availableUserDatasourceIds = useMemo(
        () => (userDatasourceDetails ?? []).filter((ds) => ds.has_access).map((ds) => ds.id),
        [userDatasourceDetails],
    );

    // Derived from the /v1/user payload so no separate access-requests call is
    // needed - the user endpoint already returns pending requests.
    const [pendingRequests, setPendingRequests] = useState<Set<string>>(new Set());
    useEffect(() => {
        const datasourceIds = new Set<string>();
        for (const req of user?.access_requests ?? []) {
            for (const ds of req.requested_datasources ?? []) {
                datasourceIds.add(ds.id);
            }
        }
        setPendingRequests(datasourceIds);
    }, [user]);

    const [deniedDatasource, setDeniedDatasource] = useState<DeniedDatasource | null>(null);
    const [selectedIndex, setSelectedIndex] = useState<string>(
        () => localStorage.getItem(route.storageKey) ?? "",
    );

    const tabKind = datasourceKindOfTab(tab, chatVectorIndex, assetsViewerEnabled);
    const tabBelongsElsewhere = tabKind !== undefined && tabKind !== kind;
    useEffect(() => {
        if (!tabBelongsElsewhere || !tabKind) return;
        const target = DATASOURCE_BROWSER_ROUTES[tabKind];
        // biome-ignore lint/suspicious/noExplicitAny: generated router types omit search params
        navigate({ to: target.path as any, search: { tab } as any, replace: true });
    }, [tabBelongsElsewhere, tabKind, tab, navigate]);

    const prevTabRef = useRef<string | undefined>(undefined);
    const hasValidatedRef = useRef(false);

    useEffect(() => {
        if (tabBelongsElsewhere || assetsViewerUnavailable) return;
        if (!isSuccess || !isUserSuccess || chatVectorIndex.length === 0) return;

        const available = visibleDatasources({
            all: chatVectorIndex,
            userRoles: availableUserDatasourceIds,
            kind,
            requireIndex: route.requireIndex,
            availableAlwaysOnIds,
            assetsViewerEnabled,
        });
        const resolution = resolveDatasourceTab({
            tab,
            available,
            all: chatVectorIndex,
            userDatasources: userDatasourceDetails ?? [],
            storedIndex: localStorage.getItem(route.storageKey),
        });
        if (!resolution) return;

        const isTabChange = tab !== prevTabRef.current;
        prevTabRef.current = tab;
        if (!isTabChange && selectedIndex && hasValidatedRef.current) return;
        hasValidatedRef.current = true;

        const { index: resolved } = resolution;
        setSelectedIndex(resolved);
        localStorage.setItem(route.storageKey, resolved);

        setDeniedDatasource(
            resolution.status === "no-access"
                ? {
                      id: resolution.datasourceId,
                      name: resolution.datasourceName,
                      description: resolution.datasourceDescription,
                      ingestion_status: resolution.datasourceIngestionStatus,
                      pending: pendingRequests.has(resolution.datasourceId),
                  }
                : null,
        );

        if (resolution.status === "not-found") {
            notifications.show({
                title: "Data source not found",
                message: "The data source in the link could not be found.",
                color: "orange",
            });
        }

        if (resolution.status !== "no-access" && tab !== resolved) {
            // biome-ignore lint/suspicious/noExplicitAny: generated router types omit search params
            navigate({ to: route.path as any, search: { tab: resolved } as any, replace: true });
        }
    }, [
        tab,
        tabBelongsElsewhere,
        assetsViewerUnavailable,
        assetsViewerEnabled,
        isSuccess,
        isUserSuccess,
        chatVectorIndex,
        userDatasourceDetails,
        availableUserDatasourceIds,
        availableAlwaysOnIds,
        pendingRequests,
        selectedIndex,
        navigate,
        kind,
        route,
    ]);

    const handleSetSelectedIndex = useCallback(
        (value: string) => {
            setSelectedIndex(value);
            localStorage.setItem(route.storageKey, value);
            setTimeout(() => {
                // biome-ignore lint/suspicious/noExplicitAny: generated router types omit search params
                navigate({ to: route.path as any, search: { tab: value } as any });
            });
        },
        [navigate, route],
    );

    const dismissDeniedDatasource = useCallback(() => {
        setDeniedDatasource(null);
        // biome-ignore lint/suspicious/noExplicitAny: generated router types omit search params
        navigate({ to: route.path as any, search: { tab: selectedIndex } as any, replace: true });
    }, [navigate, selectedIndex, route]);

    if (tabBelongsElsewhere || assetsViewerUnavailable || !selectedIndex) return null;

    return (
        <>
            <DocumentsRoute
                kind={kind}
                index={selectedIndex}
                setSelectedIndex={handleSetSelectedIndex}
            />
            {deniedDatasource && !deniedDatasource.pending && (
                <DatasourceRequestAccessModal
                    opened
                    onClose={dismissDeniedDatasource}
                    onSubmit={() => {
                        setPendingRequests((prev) => new Set([...prev, deniedDatasource.id]));
                        notifications.show({
                            title: "Request submitted",
                            message: `Your access request for ${deniedDatasource.name} has been submitted. An admin will review it shortly.`,
                            color: "green",
                        });
                        dismissDeniedDatasource();
                    }}
                    title={`Request Access to ${deniedDatasource.name}`}
                    description={deniedDatasource.description}
                    ingestion_status={deniedDatasource.ingestion_status}
                    requested_datasources={[deniedDatasource.id]}
                />
            )}
            {deniedDatasource?.pending && (
                <DatasourceAccessPendingModal
                    opened
                    onClose={dismissDeniedDatasource}
                    datasourceName={deniedDatasource.name}
                />
            )}
        </>
    );
};
